import {
  AdvanceQualificationMethod,
  ButtonComponentActionResultTypenames,
  ExternalCard,
  ExternalCardsResultTypenames,
  LendingPaymentSource,
  TagColor,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  RelativePathString,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { TabaPayEntryFlowType } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import useExternalsCards from 'features/myVaro/screens/ExternalAccounts/hooks/useExternalsCards';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import { isIOS } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const flowNameForAnalytics = 'card_management';

const ExternalCards = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'externalAccounts',
  });

  const { externalCards, fetching, errorModel, button, reExecuteQuery } =
    useExternalsCards('network-only', true);

  const hasRunRefresh = useRef(false);

  const {
    tabaPayEntryFlowType = TabaPayEntryFlowType.ExternalAccount,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    qualificationMethod,
  } = useLocalSearchParams<{
    tabaPayEntryFlowType?: TabaPayEntryFlowType;
    dueDate?: string;
    feeAmount?: string;
    amountId?: string;
    advanceAmount?: string;
    repaymentDate?: string;
    selectedAccountId?: string;
    paymentSource?: LendingPaymentSource;
    selectedAccountName?: string;
    qualificationMethod?: AdvanceQualificationMethod;
  }>();

  useFocusEffect(
    useCallback(() => {
      if (!hasRunRefresh.current) {
        reExecuteQuery();
        hasRunRefresh.current = true;
      }

      return () => {
        hasRunRefresh.current = false;
      };
    }, [reExecuteQuery]),
  );

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('externalAccountsAndCards'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={() => navigateBack()}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
      {externalCards?.__typename ===
      ExternalCardsResultTypenames.ExternalCardsNotFound ? (
        <EmptyStateScreen
          bodyText={externalCards.message}
          headlineText={externalCards.title}
          illustrationKey={externalCards.image as IllustrationKeyType}
          actionButtonText={button?.buttonText}
          actionOnPress={() => {
            if (
              button?.buttonOnClick?.__typename ===
              Typenames.RouteNavigationAction
            ) {
              router.push({
                pathname: button.buttonOnClick.route as RelativePathString,
                params: {
                  tabaPayEntryFlowType,
                  dueDate,
                  feeAmount,
                  amountId,
                  advanceAmount,
                  repaymentDate,
                  qualificationMethod,
                },
              });
            }
          }}
          analyticsId="LinkDebitCardClicked"
          customAttributes={{
            cta: 'Link a card',
            flow_name: flowNameForAnalytics,
          }}
        />
      ) : (
        <>
          <ScrollView contentContainerStyle={styles.scrollContainer}>
            {externalCards?.__typename ===
            ExternalCardsResultTypenames.ExternalCardsSuccess ? (
              <View>
                {externalCards.externalCard?.map(
                  (externalCard: ExternalCard, index: number) => {
                    return (
                      <React.Fragment key={externalCard.id}>
                        {index > 0 ? (
                          <View style={styles.dividerContainer}>
                            <HairLineDivider />
                          </View>
                        ) : null}
                        <ListItem
                          accessibilityLabel="external account"
                          key={externalCard.id}
                          primaryCopy={`${externalCard.institution.name} • ${externalCard.maskedCardNumber}`}
                          secondaryCopy={externalCard.cardNetwork}
                          onPress={() => {
                            router.push({
                              pathname:
                                '/myvaro/external-accounts/[cardId]/card-details',
                              params: {
                                cardId: externalCard.id,
                                tabaPayEntryFlowType,
                                dueDate,
                                feeAmount,
                                amountId,
                                advanceAmount,
                                repaymentDate,
                                selectedAccountId,
                                paymentSource,
                                selectedAccountName,
                              },
                            });
                          }}
                          leadingAccessory={{
                            id: '1',
                            name: VXLComponentId.ICON_AVATAR,
                            props: {
                              iconKey: 'CARD',
                              iconSize: 'MD',
                            },
                          }}
                          trailingAccessory={
                            externalCard && externalCard.tag
                              ? {
                                  id: externalCard.tag.id,
                                  name: VXLComponentId.TAG,
                                  props: {
                                    text: externalCard.tag
                                      ? externalCard.tag.text
                                      : '',
                                    type:
                                      externalCard.tag?.tagColor ===
                                      TagColor.Negative
                                        ? TagType.ERROR
                                        : TagType.SUCCESS,
                                  },
                                }
                              : undefined
                          }
                          analyticsId="ExternalCardClicked"
                          customAttributes={{
                            card_type: 'debit_card',
                            flow_name: flowNameForAnalytics,
                          }}
                        />
                      </React.Fragment>
                    );
                  },
                )}
              </View>
            ) : undefined}
          </ScrollView>
          {button?.buttonText ? (
            <View style={styles.buttonContainer}>
              <Button
                label={button.buttonText}
                onPress={() => {
                  if (
                    button?.buttonOnClick.__typename ===
                    ButtonComponentActionResultTypenames.RouteNavigationAction
                  ) {
                    router.push(button.buttonOnClick.route as Href);
                  }
                }}
                size="LARGE"
                type={button.buttonStyle}
                width="FULL"
              />
            </View>
          ) : null}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    marginBottom: theme.spacing.m,
    padding: theme.spacing.m,
    paddingVertical: isIOS ? theme.spacing.m : theme.spacing.s,
    width: '100%',
  },
  dividerContainer: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
});

export default ExternalCards;
